import type {
  DeliveryAreaSDListRequestType,
  DeliveryAreaSDListResponseType,
} from "@sellar-io/api-v2/types";
import { useQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useRemoteFeatureFlags } from "../../../useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { canLoadDeliveryAreasSellarDistribution } from "./canLoadDeliveryAreasSellarDistribution";
import {
  apiV2AssertResponse,
  ApiV2QueryOptions,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useDeliveryAreasSellarDistribution = <
  TData = DeliveryAreaSDListResponseType,
>(
  params: DeliveryAreaSDListRequestType,
  options?: ApiV2QueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const sellarDistributionSetupEnabled = useRemoteFeatureFlags().isActive(
    "sellarDistributionSetup",
  );

  const enabled = canLoadDeliveryAreasSellarDistribution({
    enabled: options?.enabled,
    supplierId: params.SupplierId,
    businessType: business?.type,
    sellarDistributionSetupEnabled,
  });

  return useQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.DeliveryAreaSellarDistribution,
      params,
    ],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `delivery-area/sellar-distribution-areas`,
        params,
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultQueryOptionsV2({
      user,
      business,
      overrideOptions: {
        ...options,
        enabled,
      },
    }),
  });
};
